'use client';

import { Chapter } from '@/hooks/useChapter';
import { Subject, Chapter as SubjectChapter } from '@/hooks/useSubject';
import AccessibilityMenu from '@/components/AccessibilityMenu/AccessibilityMenu';
import ChapterProgressIndicator from './ChapterProgressIndicator';
import Link from 'next/link';
import { slugify } from '@/utils/strings';
import { Popover, Transition } from '@headlessui/react';
import IconButton from '@/components/Button/IconButton';
import { mdiArrowLeft, mdiCogOutline, mdiMenu } from '@mdi/js';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useLayoutEffect, useState } from 'react';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import EmojiText from '@/components/EmojiText/EmojiText';
import EUCoFunding from '../Disclaimers/EUCoFunding';
import { twMerge } from 'tailwind-merge';
import { hasTagWithKey } from '@/utils/tags';

const ChaptersOverview = ({
    subject,
    currentChapter,
}: {
    subject: Subject;
    currentChapter: Chapter;
}) => {
    const reduceMotion = useReducedMotion();

    // Only adapt to focus mode when on client, so that the server can render the correct HTML
    const [isFocusModeEnabled] = useLocalStorage('isFocusModeEnabled', false);
    const [shouldAdaptToFocusMode, setShouldAdaptToFocusMode] = useState(false);
    useLayoutEffect(() => {
        setShouldAdaptToFocusMode(true);
    }, []);

    return (
        <AnimatePresence initial={false}>
            {/* Only show the aside when focus mode is disabled */}
            {!(shouldAdaptToFocusMode && isFocusModeEnabled) && (
                <motion.aside
                    key="aside"
                    layoutId="aside"
                    className="shrink-0"
                    initial={{ opacity: 0, width: reduceMotion ? 'auto' : 0 }}
                    animate={{
                        opacity: [0, 0, 1],
                        width: 'auto',
                        transition: {
                            ease: 'easeOut',
                            duration: reduceMotion ? 0.01 : 0.2,
                        },
                    }}
                    exit={{ opacity: 0, width: reduceMotion ? 'auto' : 0 }}
                >
                    <OverViewContent
                        subject={subject}
                        currentChapter={currentChapter}
                        className="relative hidden h-full overflow-hidden md:flex md:w-64 lg:w-72"
                    />
                    <Popover>
                        {({ close }) => (
                            <>
                                <div className="absolute top-4 left-4 z-50 md:hidden">
                                    <IconButton
                                        isPopoverButton
                                        isExtraContrast
                                        icon={mdiMenu}
                                        title="Inhoud"
                                    />
                                </div>

                                <Transition
                                    as="div"
                                    enter="transition duration-300 ease-out"
                                    enterFrom="motion-safe:-translate-x-full"
                                    enterTo="opacity-100"
                                    leave="transition duration-300 ease-out"
                                    leaveFrom="opacity-100"
                                    leaveTo="motion-safe:-translate-x-full"
                                    className="absolute inset-0 z-100 h-full shadow-2xl md:hidden"
                                >
                                    <Popover.Panel className="fixed inset-0 z-100 flex items-center justify-center">
                                        <OverViewContent
                                            subject={subject}
                                            currentChapter={currentChapter}
                                            className="relative flex h-full w-full flex-col overflow-hidden font-bold"
                                            onClose={() => close()}
                                        />
                                    </Popover.Panel>
                                </Transition>
                            </>
                        )}
                    </Popover>
                    <AccessibilityMenu
                        subject={subject}
                        panelClassName="z-100 md:hidden"
                        buttonClassName="absolute top-4 right-4 z-50 text-white md:invisible md:hidden md:opacity-0"
                        buttonIcon={mdiCogOutline}
                        buttonIsExtraContrast
                    />
                </motion.aside>
            )}
        </AnimatePresence>
    );
};

const OverViewContent = ({
    subject,
    currentChapter,
    className,
    onClose,
}: {
    subject: Subject;
    currentChapter: Chapter;
    className?: string;
    onClose?: () => void;
}) => {
    const isCoFundedByEU = hasTagWithKey(subject, 'eu-medegefinancierd');

    return (
        <div
            className={twMerge(
                'flex flex-col max-h-full bg-primary-700 md:bg-gradient-to-b from-white/95 to-white/90 overflow-x-hidden',
                className,
            )}
        >
            <h2 className="flex items-center gap-4 from-transparent pl-4 p-2 text-xl bg-primary-600 text-white shadow-md md:justify-between">
                {onClose && (
                    <IconButton
                        className="text-white"
                        onClick={onClose}
                        icon={mdiArrowLeft}
                        title="Inhoud sluiten"
                    />
                )}
                <span className="py-2">Inhoud</span>
                <div className="hidden md:block">
                    <AccessibilityMenu subject={subject} />
                </div>
            </h2>
            <nav
                aria-label="Hoofdstukken"
                className="grow mb-auto overflow-x-hidden overflow-y-auto"
            >
                <ol>
                    {subject.chapters.map((chapter, index) => (
                        <ChapterListItem
                            key={chapter.id}
                            subject={subject}
                            chapter={chapter}
                            currentChapter={currentChapter}
                            isFirst={index === 0}
                            isLast={index === subject.chapters.length - 1}
                            onClick={onClose}
                        />
                    ))}
                </ol>
            </nav>
            {isCoFundedByEU && (
                <div className="mt-auto pt-2">
                    <EUCoFunding />
                </div>
            )}
        </div>
    );
};

const ChapterListItem = ({
    subject,
    chapter,
    currentChapter,
    isFirst,
    isLast,
    onClick,
}: {
    subject: Subject;
    chapter: SubjectChapter;
    currentChapter: Chapter;
    isFirst: boolean;
    isLast: boolean;
    onClick?: () => void;
}) => {
    const reducedMotion = useReducedMotion();
    const { subjectProgress } = useSubjectProgress(subject.id);
    const [firstUncompletedPageId, setFirstUncompletedPageId] = useState(
        chapter.page_ids[0],
    );

    // Find the first page that is not completed
    useLayoutEffect(() => {
        setFirstUncompletedPageId(
            chapter.page_ids.find(
                (page_id) => !subjectProgress.pages?.[page_id],
            ) ?? chapter.page_ids[0],
        );
    }, [chapter, subjectProgress]);

    return (
        <li key={chapter.id}>
            <Link
                href={`/story/${subject.id}/${slugify(subject.title)}/${
                    chapter.id
                }/${slugify(chapter.title)}/${firstUncompletedPageId}`}
                passHref={true}
                className={`relative ${
                    chapter.id === currentChapter.id
                        ? 'pointer-events-none bg-gradient-to-r from-white/10 md:from-white md:via-white/75 to-white/0 shadow-sm'
                        : ''
                } group block border-b-2 md:border-b-0 border-primary-600/25 p-4 text-white transition-all hover:bg-white/10 md:border-primary-500/10 md:text-slate-700 md:hover:bg-primary-500/10 focus-visible:inner-border-2 focus-visible:inner-border-primary-700`}
                onClick={() => onClick?.()}
            >
                <span className="relative flex origin-left gap-2.5 transition-all motion-safe:group-hover:scale-110 motion-safe:group-active:scale-95">
                    <ChapterProgressIndicator
                        subject={subject}
                        chapter={chapter}
                    />
                    <span className="line-clamp-1">
                        <EmojiText>{chapter.title}</EmojiText>
                    </span>
                    {chapter.id === currentChapter.id && (
                        <>
                            <span className="sr-only">(huidig hoofdstuk)</span>
                            <motion.div
                                layoutId={
                                    reducedMotion
                                        ? ''
                                        : 'current-chapter-indicator-front'
                                }
                                layoutDependency={currentChapter.id}
                                className="absolute top-2.5 -left-2.5 -my-4 h-9 w-1 rounded-full bg-primary-100 md:bg-primary-500 shadow-sm"
                                transition={{
                                    type: 'spring',
                                    bounce: 0.4,
                                    duration: 1,
                                }}
                            />
                        </>
                    )}
                </span>
                {!isFirst && (
                    <div className="absolute top-0 left-[1.625rem] -z-1 h-4 w-1 bg-primary-500/20" />
                )}
                {!isLast && (
                    <div className="absolute bottom-0 left-[1.625rem] -z-1 h-4 w-1 bg-primary-500/20" />
                )}
            </Link>
        </li>
    );
};

export default ChaptersOverview;
